/**
 * Helpers for TS1 v3 / TS3 v2 / TS5 v2 split registration fields vs legacy {@link aircraftSimReg}.
 */

import {
	AbstractControl,
	UntypedFormArray,
	UntypedFormBuilder,
	UntypedFormControl,
	UntypedFormGroup,
	ValidationErrors,
	ValidatorFn,
	Validators,
} from "@angular/forms";
import { isEts1SessionV2 } from "src/app/cbta-web/ets1/session/ets1-session-form.utils";
import { FlightSectorLeg, FlightSimulatorDetails, FormDataOptions, SimulatorDetails } from "src/app/models/models";

type FlightSimSummaryInput = FlightSimulatorDetails | null | undefined;

/** Exact length for TS1 v3 Departure / Destination ICAO fields. */
export const ICAO_MAX_LENGTH = 4;

/** Max Departure/Destination sector rows on TS1 v3 Step 1. */
export const MAX_FLIGHT_SECTORS = 6;

/** Strip non-letters, uppercase, and truncate to {@link ICAO_MAX_LENGTH}. */
export function normalizeIcaoInput(raw: unknown): string {
	return (raw ?? "")
		.toString()
		.replace(/[^A-Za-z]/g, "")
		.toUpperCase()
		.slice(0, ICAO_MAX_LENGTH);
}

/** Exact 4-letter ICAO code (empty left to {@link Validators.required}). */
function icaoExactLengthValidator(): ValidatorFn {
	return (control: AbstractControl): ValidationErrors | null => {
		const value = (control.value ?? "").toString();
		if (!value) return null;
		if (!/^[A-Za-z]{4}$/.test(value)) {
			return { icaoInvalid: true };
		}
		return null;
	};
}

/** Validators for Departure / Destination ICAO: exactly 4 letters. */
export function icaoFieldValidators(required = true): ValidatorFn[] {
	const validators: ValidatorFn[] = [icaoExactLengthValidator(), Validators.maxLength(ICAO_MAX_LENGTH)];
	if (required) validators.unshift(Validators.required);
	return validators;
}

/** One sector FormGroup for the TS1 v3 multi-sector table. */
export function createFlightSectorGroup(
	fb: UntypedFormBuilder,
	leg: FlightSectorLeg = {},
	required = true,
): UntypedFormGroup {
	return fb.group({
		departureIcao: [normalizeIcaoInput(leg.departureIcao), icaoFieldValidators(required)],
		destinationIcao: [normalizeIcaoInput(leg.destinationIcao), icaoFieldValidators(required)],
	});
}

/** Normalize raw sector legs (API/draft/form) into a capped, cleaned list. */
export function normalizeFlightSectorLegs(raw: unknown): FlightSectorLeg[] {
	if (!Array.isArray(raw)) return [];
	const legs: FlightSectorLeg[] = [];
	for (const item of raw) {
		if (!item || typeof item !== "object") continue;
		const record = item as Record<string, unknown>;
		legs.push({
			departureIcao: normalizeIcaoInput(record["departureIcao"]),
			destinationIcao: normalizeIcaoInput(record["destinationIcao"]),
		});
		if (legs.length >= MAX_FLIGHT_SECTORS) break;
	}
	return legs;
}

/**
 * Resolve sectors for form/API: prefer explicit `sectors`, else legacy single dep/dest
 * (and optionally split airportRoute).
 */
export function resolveFlightSectorsFromDetails(fs: Record<string, unknown> | null | undefined): FlightSectorLeg[] {
	const raw = fs ?? {};
	const fromArray = normalizeFlightSectorLegs(raw["sectors"]);
	if (fromArray.length > 0) {
		return fromArray;
	}

	let departureIcao = normalizeIcaoInput(raw["departureIcao"]);
	let destinationIcao = normalizeIcaoInput(raw["destinationIcao"]);
	const airportRoute = asFlightSimString(raw["airportRoute"]);

	if (!departureIcao && !destinationIcao && airportRoute) {
		const split = splitLegacyAirportRoute(airportRoute);
		departureIcao = normalizeIcaoInput(split.departure);
		destinationIcao = normalizeIcaoInput(split.destination);
	}

	if (departureIcao || destinationIcao) {
		return [{ departureIcao, destinationIcao }];
	}
	return [];
}

/** Ensure `flightSimulatorDetails.sectors` FormArray matches the given legs. */
export function applyFlightSectorsToForm(
	fs: UntypedFormGroup,
	legs: FlightSectorLeg[],
	opts?: { required?: boolean; fb?: UntypedFormBuilder },
): UntypedFormArray {
	const fb = opts?.fb ?? new UntypedFormBuilder();
	const required = opts?.required ?? true;
	const resolved = legs.length > 0 ? legs.slice(0, MAX_FLIGHT_SECTORS) : [{ departureIcao: "", destinationIcao: "" }];

	let sectors = fs.get("sectors") as UntypedFormArray | null;
	if (!sectors) {
		sectors = fb.array([]) as UntypedFormArray;
		fs.addControl("sectors", sectors, { emitEvent: false });
	}

	while (sectors.length > resolved.length) {
		sectors.removeAt(sectors.length - 1, { emitEvent: false });
	}
	while (sectors.length < resolved.length) {
		sectors.push(createFlightSectorGroup(fb, {}, required), { emitEvent: false });
	}

	resolved.forEach((leg, i) => {
		const group = sectors!.at(i) as UntypedFormGroup;
		group.get("departureIcao")?.setValue(normalizeIcaoInput(leg.departureIcao), { emitEvent: false });
		group.get("destinationIcao")?.setValue(normalizeIcaoInput(leg.destinationIcao), { emitEvent: false });
		group.get("departureIcao")?.setValidators(icaoFieldValidators(required));
		group.get("destinationIcao")?.setValidators(icaoFieldValidators(required));
		group.get("departureIcao")?.updateValueAndValidity({ emitEvent: false });
		group.get("destinationIcao")?.updateValueAndValidity({ emitEvent: false });
	});

	const first = resolved[0] ?? { departureIcao: "", destinationIcao: "" };
	ensureControl(fs, "departureIcao").setValue(normalizeIcaoInput(first.departureIcao), { emitEvent: false });
	ensureControl(fs, "destinationIcao").setValue(normalizeIcaoInput(first.destinationIcao), {
		emitEvent: false,
	});

	sectors.updateValueAndValidity({ emitEvent: false });
	return sectors;
}

/** Patch flight-sim form including rebuilding `sectors` FormArray when present. */
export function patchFlightSimulatorDetailsOntoForm(
	fs: UntypedFormGroup,
	raw: Record<string, unknown>,
	opts?: { required?: boolean; fb?: UntypedFormBuilder },
): void {
	const { sectors: _sectors, ...rest } = raw;
	fs.patchValue(rest, { emitEvent: false });
	if ("sectors" in raw || raw["departureIcao"] != null || raw["destinationIcao"] != null) {
		applyFlightSectorsToForm(fs, resolveFlightSectorsFromDetails(raw), opts);
	}
}

function ensureControl(fs: UntypedFormGroup, name: string): UntypedFormControl {
	let ctrl = fs.get(name) as UntypedFormControl | null;
	if (!ctrl) {
		ctrl = new UntypedFormControl("");
		fs.addControl(name, ctrl, { emitEvent: false });
	}
	return ctrl;
}

/** TS1 v3 / TS3 v2 / TS5 v2 split registration UI is active when mode is set. */
export function hasSplitFlightSimDetails(fs: FlightSimSummaryInput): boolean {
	const mode = (fs?.aircraftOrSimulator ?? "").toString();
	return mode === "aircraft" || mode === "simulator";
}

export type FlightSimSummaryLayoutOptions = {
	/** TS-1 v3/v4 always uses split ICAO layout regardless of admin session catalog. */
	forceV3FlightDetails?: boolean;
	ets1SessionVersion?: number;
};

/** True when classroom / ICAO sector summary layout applies (ETS-3.5 admin catalog or TS-1 v3/v4). */
export function usesEts35FlightSummaryLayout(options?: FlightSimSummaryLayoutOptions): boolean {
	return !!options?.forceV3FlightDetails || isEts1SessionV2({ ets1SessionVersion: options?.ets1SessionVersion });
}

/** TS1 v3 uses split ICAO/route fields instead of a single airportRoute. */
export function hasTs1V3FlightDetails(fs: FlightSimSummaryInput | Record<string, unknown> | null | undefined): boolean {
	if (!fs) return false;
	const record = fs as Record<string, unknown>;
	const sectors = normalizeFlightSectorLegs(record["sectors"]);
	if (sectors.length > 0) return true;
	if (record["departureIcao"] || record["destinationIcao"] || record["otherAirportsRoute"]) {
		return true;
	}
	const typed = fs as FlightSimSummaryInput;
	return !!(
		(typed?.sectors && typed.sectors.length > 0) ||
		typed?.departureIcao ||
		typed?.destinationIcao ||
		typed?.otherAirportsRoute
	);
}

export function hasTs1V3FlightDetailsForDisplay(
	fs: FlightSimSummaryInput | Record<string, unknown> | null | undefined,
	options?: FlightSimSummaryLayoutOptions,
): boolean {
	return usesEts35FlightSummaryLayout(options) && hasTs1V3FlightDetails(fs);
}

export function isClassroomFlightSimSummary(
	fs: FlightSimSummaryInput | null | undefined,
	options?: FlightSimSummaryLayoutOptions,
): boolean {
	if (!usesEts35FlightSummaryLayout(options)) return false;
	return (fs?.aircraftOrSimulator ?? "").toString().toLowerCase() === "classroom";
}

export function flightSimSummarySectionTitle(
	fs: FlightSimSummaryInput,
	options?: FlightSimSummaryLayoutOptions,
): string {
	const mode = (fs?.aircraftOrSimulator ?? "").toString();
	if (isClassroomFlightSimSummary(fs, options)) return "Classroom Details";
	if (hasTs1V3FlightDetailsForDisplay(fs, options)) return "Flight Details";
	if (mode === "aircraft") return "Aircraft Details";
	if (mode === "simulator") return "Simulator Details";
	return "Flight / Simulator Details";
}

export function flightSimSummaryRegistrationLabel(
	fs: FlightSimSummaryInput,
	options?: FlightSimSummaryLayoutOptions,
): string {
	const mode = (fs?.aircraftOrSimulator ?? "").toString();
	if (isClassroomFlightSimSummary(fs, options)) return "Classroom";
	if (mode === "aircraft") return "Aircraft Registration";
	if (mode === "simulator") return "Simulator Registration";
	return "ACFT / Sim Registration";
}

export function flightSimSummaryAcTypeLabel(fs: FlightSimSummaryInput): string {
	return hasSplitFlightSimDetails(fs) ? "Aircraft Type" : "AC / Type";
}

export function flightSimSummaryDisplayValue(fs: FlightSimSummaryInput, key: keyof FlightSimulatorDetails): string {
	const v = asFlightSimString(fs?.[key]);
	return v || "-";
}

export function flightSimSummarySectorNumber(fs: FlightSimSummaryInput): string {
	return asFlightSimString(fs?.sectorNumber ?? fs?.sectorNo) || "-";
}

/** Display legs for summary/PDF (falls back to single dep/dest). */
export function flightSimSummarySectors(fs: FlightSimSummaryInput): FlightSectorLeg[] {
	return resolveFlightSectorsFromDetails((fs ?? {}) as Record<string, unknown>);
}

/** Whether to show Other Airport(s)/Route(s) (simulator mode, or legacy non-empty value). */
export function flightSimSummaryShowOtherAirport(
	fs: FlightSimSummaryInput,
	options?: FlightSimSummaryLayoutOptions,
): boolean {
	if (!hasTs1V3FlightDetailsForDisplay(fs, options)) return false;
	const mode = (fs?.aircraftOrSimulator ?? "").toString().toLowerCase();
	const value = asFlightSimString(fs?.otherAirportsRoute);
	if (mode === "simulator") return true;
	return !!value;
}

/** Single display/save value for reports and backward-compatible APIs. */
export function resolveAircraftSimRegFromFlightSimulatorDetails(
	fs: Record<string, unknown> | null | undefined,
): string {
	if (!fs) return "";
	const mode = (fs["aircraftOrSimulator"] ?? "aircraft").toString();
	if (mode === "simulator") {
		return asFlightSimString(fs["simulatorRegistration"] ?? fs["aircraftSimReg"]).toUpperCase();
	}
	return (fs["aircraftRegistration"] ?? fs["aircraftSimReg"] ?? "").toString();
}

/** Summary display registration (typed {@link FlightSimulatorDetails} from getSummary API). */
export function resolveFlightSimRegistrationForSummary(
	fs: FlightSimSummaryInput,
	options?: FlightSimSummaryLayoutOptions,
): string {
	if (!fs) return "";
	const mode = (fs.aircraftOrSimulator ?? "").toString();
	if (isClassroomFlightSimSummary(fs, options)) {
		if (fs.classroom === "free_text") {
			return (fs.classroomFreeText ?? fs.airportRoute ?? fs.acftSimReg ?? "").toString();
		}
		return (fs.classroom ?? fs.airportRoute ?? fs.acftSimReg ?? "").toString();
	}
	if (mode === "simulator") {
		return (fs.simulatorRegistration ?? fs.acftSimReg ?? "").toString().toUpperCase();
	}
	if (mode === "aircraft") {
		return (fs.aircraftRegistration ?? fs.acftSimReg ?? "").toString();
	}
	return (fs.acftSimReg ?? "").toString();
}

function asFlightSimString(v: unknown): string {
	if (v == null) return "";
	if (typeof v === "string") return v;
	if (typeof v === "object" && v !== null && ("description" in v || "code" in v)) {
		const o = v as { description?: string; code?: string };
		return (o.description ?? o.code ?? "").toString();
	}
	return String(v);
}

/** Attempt to split legacy airportRoute (e.g. "KUL-PEN") into departure/destination. */
function splitLegacyAirportRoute(airportRoute: string): { departure: string; destination: string } {
	const trimmed = airportRoute.trim();
	if (!trimmed) return { departure: "", destination: "" };
	const parts = trimmed
		.split(/[-/]/)
		.map((p) => p.trim())
		.filter(Boolean);
	if (parts.length >= 2) {
		return { departure: parts[0], destination: parts[1] };
	}
	return { departure: trimmed, destination: "" };
}

/** Map a draft/API string (or partial object) to an autocomplete option for reactive forms. */
export function resolveFlightSimAutocompleteOption(
	value: unknown,
	options: FormDataOptions[] | undefined,
): FormDataOptions | string {
	if (value == null || value === "") return "";
	if (typeof value === "object" && value !== null && ("code" in value || "description" in value)) {
		const o = value as FormDataOptions;
		const code = (o.code ?? "").toString();
		const found = options?.find((opt) => opt.code === code);
		return found ?? o;
	}
	const str = asFlightSimString(value);
	if (!str) return "";
	const found = options?.find((opt) => opt.code === str || opt.description === str);
	return found ?? { code: str, description: str };
}

/** Shape to persist on candidates (TS1 v3 / TS3 v2 / TS5 v2 split fields). Legacy {@link aircraftSimReg} is always empty. */
export function buildFlightSimulatorDetailsPayload(fs: Record<string, unknown> | null | undefined): SimulatorDetails {
	const raw = fs ?? {};
	const mode = (raw["aircraftOrSimulator"] ?? "aircraft").toString();
	if (mode === "classroom") {
		const classroom = asFlightSimString(raw["classroom"]);
		const classroomFreeText = asFlightSimString(raw["classroomFreeText"]).slice(0, 100);
		const presetClassroom = classroom && classroom !== "free_text" ? classroom : "";
		const label = (classroomFreeText || presetClassroom || asFlightSimString(raw["airportRoute"])).slice(0, 100);
		return {
			aircraftOrSimulator: "classroom",
			classroom,
			classroomFreeText,
			airportRoute: label,
			acType: "",
			accumulatedSectors: "",
			sectorNo: "",
			aircraftRegistration: "",
			simulatorRegistration: "",
			aircraftSimReg: "",
		};
	}
	const aircraftRegistration = (raw["aircraftRegistration"] ?? "").toString();
	const simulatorRegistration = asFlightSimString(raw["simulatorRegistration"]).toUpperCase();
	const base: SimulatorDetails = {
		acType: asFlightSimString(raw["acType"]),
		accumulatedSectors: asFlightSimString(raw["accumulatedSectors"]),
		sectorNo: asFlightSimString(raw["sectorNo"] ?? raw["sectorNumber"]),
		aircraftOrSimulator: mode,
		aircraftRegistration: mode === "aircraft" ? aircraftRegistration : "",
		simulatorRegistration: mode === "simulator" ? simulatorRegistration : "",
		aircraftSimReg: "",
	};

	const airportRoute = asFlightSimString(raw["airportRoute"]);
	const sectors = resolveFlightSectorsFromDetails(raw);
	const otherAirportsRoute = mode === "simulator" ? asFlightSimString(raw["otherAirportsRoute"]).toUpperCase() : "";

	const hasV3Keys =
		raw["sectors"] != null ||
		raw["departureIcao"] != null ||
		raw["destinationIcao"] != null ||
		raw["otherAirportsRoute"] != null ||
		hasTs1V3FlightDetails(raw as FlightSimSummaryInput);

	if (hasV3Keys) {
		let resolvedSectors = sectors;
		if (resolvedSectors.length === 0 && airportRoute) {
			const split = splitLegacyAirportRoute(airportRoute);
			resolvedSectors = [
				{
					departureIcao: normalizeIcaoInput(split.departure),
					destinationIcao: normalizeIcaoInput(split.destination),
				},
			];
		}
		if (resolvedSectors.length === 0) {
			resolvedSectors = [{ departureIcao: "", destinationIcao: "" }];
		}

		const first = resolvedSectors[0];
		return {
			...base,
			sectors: resolvedSectors,
			departureIcao: first.departureIcao ?? "",
			destinationIcao: first.destinationIcao ?? "",
			...(mode === "simulator" ? { otherAirportsRoute } : { otherAirportsRoute: "" }),
		};
	}

	return {
		...base,
		airportRoute,
	};
}

/** When patching reactive forms from API / draft (may only have {@link aircraftSimReg}). */
export function flightSimulatorDetailsForFormPatch(
	fs: Record<string, unknown> | null | undefined,
	aircraftTypeOptions?: FormDataOptions[],
) {
	const mode = (fs?.["aircraftOrSimulator"] ?? "aircraft").toString();
	if (mode === "classroom") {
		return {
			aircraftOrSimulator: "classroom",
			classroom: asFlightSimString(fs?.["classroom"]),
			classroomFreeText: asFlightSimString(fs?.["classroomFreeText"]),
			airportRoute: asFlightSimString(fs?.["airportRoute"]),
			acType: "",
			accumulatedSectors: "",
			sectorNo: "",
			aircraftRegistration: "",
			simulatorRegistration: "",
		};
	}
	const legacy = (fs?.["aircraftSimReg"] ?? "").toString();

	const otherAirportsRoute = asFlightSimString(fs?.["otherAirportsRoute"]);
	const airportRoute = asFlightSimString(fs?.["airportRoute"]);
	const sectors = resolveFlightSectorsFromDetails(fs);

	const useV3 =
		hasTs1V3FlightDetails(fs) ||
		sectors.length > 0 ||
		!!(asFlightSimString(fs?.["departureIcao"]) || asFlightSimString(fs?.["destinationIcao"]) || otherAirportsRoute);

	const common = {
		acType: resolveFlightSimAutocompleteOption(fs?.["acType"], aircraftTypeOptions),
		accumulatedSectors: fs?.["accumulatedSectors"] ?? "",
		sectorNo: fs?.["sectorNo"] ?? fs?.["sectorNumber"] ?? "",
		aircraftOrSimulator: mode,
		aircraftRegistration: (fs?.["aircraftRegistration"] ?? (mode === "aircraft" ? legacy : "")).toString(),
		simulatorRegistration: (fs?.["simulatorRegistration"] ?? (mode === "simulator" ? legacy : "")).toString(),
	};

	if (useV3) {
		const resolved =
			sectors.length > 0
				? sectors
				: airportRoute
					? (() => {
							const split = splitLegacyAirportRoute(airportRoute);
							return [
								{
									departureIcao: normalizeIcaoInput(split.departure),
									destinationIcao: normalizeIcaoInput(split.destination),
								},
							];
						})()
					: [{ departureIcao: "", destinationIcao: "" }];
		const first = resolved[0];
		return {
			...common,
			sectors: resolved,
			departureIcao: first.departureIcao ?? "",
			destinationIcao: first.destinationIcao ?? "",
			otherAirportsRoute: mode === "aircraft" ? "" : otherAirportsRoute,
		};
	}

	return {
		...common,
		airportRoute: airportRoute,
	};
}
